import { useIntl } from 'react-intl';

import { IconArrowUp, IconArrowDown } from '@xing-com/icons';
import { Menu } from '@xing-com/menu';
import { usePopOver } from '@xing-com/pop-over';
import { spaceS } from '@xing-com/tokens';

import { ContentSwitcherButton } from '../content-switcher-button';
import { ContentSwitcherMoreItem } from '../content-switcher-more-item';
import { useKeyboardNavigation } from '../hooks/use-keyboard-navigation';

import type { ContentSwitcherMoreProps } from './content-switcher-more.types';

export const ContentSwitcherMore: React.FC<ContentSwitcherMoreProps> = ({
  activeItemIndex,
  data,
  isFixed,
  selected,
  size,
  sizeConfined,
  sizeWide,
  variant,
  ...props
}) => {
  const intl = useIntl();
  const popOver = usePopOver();
  const Icon = popOver.getState() ? IconArrowUp : IconArrowDown;
  const iconSize = size === 'small' ? 18 : 24;
  const label = selected
    ? data[activeItemIndex].copy?.toString()
    : intl.formatMessage({
        id: 'CONTENT_SWITCHER_MORE',
        defaultMessage: 'More',
      });
  const { handleKeyDown } = useKeyboardNavigation({
    selector: '[role="menuitem"]',
  });

  const handleClick = (): void => popOver.togglePopOver();

  return (
    <>
      <ContentSwitcherButton
        activeItemIndex={activeItemIndex}
        aria-haspopup="true"
        aria-expanded={popOver.show}
        aria-label={label}
        data-qa="more-button"
        data-testid="more-button"
        icon={() => (
          <Icon aria-hidden="true" height={iconSize} width={iconSize} />
        )}
        ref={popOver.triggerRef}
        onClick={handleClick}
        selected={selected}
        size={size}
        sizeConfined={sizeConfined}
        sizeWide={sizeWide}
        variant={variant}
        {...props}
      >
        {label}
      </ContentSwitcherButton>
      <Menu
        isInFlow={isFixed}
        isStatic={isFixed}
        offset={parseInt(spaceS)}
        onOutsideClick={popOver.handleHide}
        role="menu"
        show={popOver.show}
        triggerRef={popOver.triggerRef}
        onKeyDown={handleKeyDown}
      >
        {data.map((item, index) => (
          <ContentSwitcherMoreItem
            activeItemIndex={activeItemIndex}
            data-qa={`more-menu-item-${index}`}
            data-testid={`more-menu-item-${index}`}
            key={index}
            popOver={popOver}
            selected={activeItemIndex === index}
            {...item}
          >
            {item.copy}
          </ContentSwitcherMoreItem>
        ))}
      </Menu>
    </>
  );
};
